/* eslint-disable @stripe-internal/stripe/no-mixed-react-exports */
import {ComponentType, ReactNode, useMemo, useEffect} from 'react';
import {useSetExperimentsPageId} from 'mkt-ssr/lib/experiments';
import {PageMetadata, PageMetadataProvider} from 'mkt-ssr/lib/page/metadata';
import {PageInitializer} from 'mkt-ssr/lib/page/initialize/PageInitializer';
import {
  SerializedServerSideProp,
  SerializedServerSideProps,
} from 'mkt-ssr/lib/page/serializeProps';
import {
  DenormalizedEntity,
  isLink,
  normalizedAsset,
  normalizedEntry,
  visitFields,
  EntityManager,
  NormalizedManager,
  DenormalizedManager,
} from '@stripe-internal/mkt-contentful';
import {PageHead} from 'mkt-ssr/components/PageHead/PageHead';
import {Layout} from 'mkt-ssr/components/Layout/Layout';
import {
  DEFAULT_PAGE_CONFIG,
  RuntimePageConfig,
} from 'mkt-ssr/lib/page/PageConfig';
import {z} from 'zod';
import {NormalizedManagerProvider} from 'mkt-ssr/lib/contentful/normalization/context';
import {ErrorBoundary} from '@sail/observability';
import {AppErrorView} from 'mkt-ssr/components/AppErrorView';
import {UiEngineProvider} from 'mkt-ssr/components/UiEngineProvider';
import {IntlHook, useIntl} from '@sail/intl';

interface CreatePageParameters<T> {
  metadata(props: T, intl: IntlHook): PageMetadata;
  pageConfig?: Partial<RuntimePageConfig>;
}

function normalizeGeneric<T>(manager: EntityManager): z.ZodType<T> {
  return normalizedEntry(
    manager,
    z
      .object({})
      .passthrough()
      .transform(
        (v, ctx) =>
          visitFields(v, {
            visitLink(link) {
              return normalizeGeneric(manager).parse(link, ctx);
            },
          }) as T,
      ) as z.ZodType<DenormalizedEntity>,
  ).or(normalizedAsset(manager)) as unknown as z.ZodType<T>;
}

function normalizePageProp<T>(
  prop: SerializedServerSideProp<T>,
  manager: EntityManager,
): T {
  if (Array.isArray(prop)) {
    return prop.map((v) => normalizePageProp(v, manager)) as T;
  } else if (typeof prop === 'object' && !!prop) {
    if (isLink(prop)) {
      return normalizeGeneric<T>(manager).parse(prop);
    } else {
      return Object.entries(prop).reduce(
        (prev, [key, value]) => ({
          ...prev,
          [key]: normalizePageProp(value, manager),
        }),
        {},
      ) as T;
    }
  } else {
    return prop as T;
  }
}

function normalizePageProps<T>(
  {$denormalized, ...rest}: SerializedServerSideProps<T>,
  manager: EntityManager,
): T & {children?: ReactNode} {
  manager.denormalized.add({
    entries: new Map(Object.entries($denormalized?.entries || {})),
    assets: new Map(Object.entries($denormalized?.assets || {})),
  });
  return normalizePageProp(
    rest as SerializedServerSideProp<T>,
    manager,
  ) as T & {children?: ReactNode};
}

export function createPage<T>(
  Component: ComponentType<React.PropsWithChildren<T>>,
  {metadata, pageConfig = DEFAULT_PAGE_CONFIG}: CreatePageParameters<T>,
) {
  return function Page(props: SerializedServerSideProps<T>) {
    const intl = useIntl();
    const setExperimentsPageId = useSetExperimentsPageId();
    const manager = useMemo(
      () => ({
        normalized: new NormalizedManager(),
        denormalized: new DenormalizedManager(),
      }),
      [],
    );
    const normalizedProps = useMemo(
      () => normalizePageProps<T>(props, manager),
      [props, manager],
    );
    const initializedMetadata = useMemo(
      () => ({
        ...metadata(normalizedProps, intl),
        ...DEFAULT_PAGE_CONFIG,
        ...pageConfig,
      }),
      [normalizedProps, intl],
    );

    // Sets the analytics page id so that <ExperimentsProvider> can access it
    useEffect(() => {
      setExperimentsPageId(initializedMetadata.analytics.id);
    }, [initializedMetadata.analytics.id, setExperimentsPageId]);

    const uiEngine = pageConfig.uiEngine ?? 'helm';

    return (
      <UiEngineProvider uiEngine={uiEngine}>
        <NormalizedManagerProvider value={manager.normalized}>
          <PageMetadataProvider metadata={initializedMetadata}>
            <PageInitializer />
            <PageHead />
            <Layout>
              <ErrorBoundary uuid="2AFZML00" errorView={AppErrorView}>
                <Component {...normalizedProps} />
              </ErrorBoundary>
            </Layout>
          </PageMetadataProvider>
        </NormalizedManagerProvider>
      </UiEngineProvider>
    );
  };
}
